import mitt, { Emitter, EventType } from 'mitt';

import { assertUnreachable, scalarArrayCompare } from "@/common/utils";
import { ConditionComparatorsType, SurveyLogic, SurveyLogicAction, SurveyLogicCondition, SurveyLogicRule } from "@/surveys/logic/types";
import { isLogicRuleValid } from "@/surveys/logic/utils";
import { useResponseStore } from "./stores/responseStore";
import { QuestionType, SubResponseType } from "@/common/interfaces";
import { ChoiceResponse } from "./responses";

export declare type QuestionEffect = {
	questionUuid: string
	visible: boolean;
};

function genericEvaluator(
	valueA: string | number | Array<string> | Array<number> | null,
	valueB: string | number | Array<string> | Array<number> | null,
	comparator: ConditionComparatorsType
): boolean {
	switch(comparator) {
		case '=':
			if (valueA === null || valueB === null) {
				return false;
			}

			if (Array.isArray(valueA) && Array.isArray(valueB)) {
				return scalarArrayCompare<string | number>(valueA, valueB);
			} else if (Array.isArray(valueB) && valueB.length === 1) {
				return valueA === valueB[0];
			}

			return valueA === valueB;
		break;
		case '!=':
			if (valueA === null || valueB === null) {
				return true;
			}

			if (Array.isArray(valueA) && Array.isArray(valueB)) {
				return !scalarArrayCompare<string | number>(valueA, valueB);
			} else if (Array.isArray(valueB) && valueB.length === 1) {
				return valueA !== valueB[0];
			}

			return valueA !== valueB;
		break;
		case '<':
			if (valueA === null || valueB === null) {
				return false;
			}

			if (Array.isArray(valueB)) {
				if (valueB.length === 0) {
					return false;
				}
				return valueB.every((rv) => rv < valueA);
			}

			return valueB < valueA;
		break;
		case '>':
			if (valueA === null || valueB === null) {
				return false;
			}

			if (Array.isArray(valueB)) {
				if (valueB.length === 0) {
					return false;
				}
				return valueB.every((rv) => rv > valueA);
			}

			return valueB > valueA;
		break;
		case '>=':
			if (valueA === null || valueB === null) {
				return false;
			}

			if (Array.isArray(valueB)) {
				if (valueB.length === 0) {
					return false;
				}
				return valueB.every((rv) => rv >= valueA);
			}

			return valueB >= valueA;
		break;
		case '<=':
			if (valueA === null || valueB === null) {
				return false;
			}

			if (Array.isArray(valueB)) {
				if (valueB.length === 0) {
					return false;
				}
				return valueB.every((rv) => rv <= valueA);
			}

			return valueB <= valueA;
		break;
		case 'between':
		break;
		case 'not null':
			return valueB !== null;
		break;
		case 'null':
			return valueB === null;
		break;
		case 'in':
			if (
				valueA === null || valueB === null
			) {
				return false;
			}

			if (typeof valueA === 'string' && typeof valueB === 'string') {
				return valueB.includes(valueA);
			}

			if (
				!Array.isArray(valueA)
			) {
				return false;
			}

			if (!Array.isArray(valueB)) {
				return (valueA as Array<string | number>).includes(valueB);
			}

			return valueA.some(
				(v: string | number) => (valueB! as Array<string | number>).includes(v)
			);
		break;
		case 'not in':
			if (
				valueA === null || valueB === null
			) {
				return true;
			}

			if (typeof valueA === 'string' && typeof valueB === 'string') {
				return !valueA.includes(valueB);
			}

			if (
				!Array.isArray(valueA)
			) {
				return false;
			}

			if (!Array.isArray(valueB)) {
				return !(valueA as Array<string | number>).includes(valueB);
			}

			return valueA.every(
				(v: string | number) => !(valueB! as Array<string | number>).includes(v)
			);
		break;
		default:
			assertUnreachable(comparator);
	}

	return false;
}

export class LogicHandler {
	logic: Array<SurveyLogicRule> | null = null;

	events: Emitter<Record<EventType, unknown>>;

	/**
	 * Store the rules that were passed through `isLogicRuleValid`
	 * and therefore are found to be valid rules type wise.
	 * However not necessarily valid logically! (aka a question hiding a question on a previous page)
	 */
	validRules: Array<SurveyLogicRule> = [];

	/**
	 * Maps question uuids to LogicRules where that question is a condition
	 * to the rule.
	 */
	questionConditionMap: {[qUuid: string]: Array<SurveyLogicRule>} = {};

	/**
	 * A map of questions visibility states.
	 * The front end only knows the questions it gets from the server
	 * so if a future questions visibility should be modified it needs
	 * to be stored in advance
	 */
	questionEffectsMap: {[qUuid: string]: QuestionEffect} = {};

	constructor() {
		this.events = mitt();
	}

	setLogic(logic: Array<SurveyLogicRule> | null): void {
		this.logic = logic;
		this.validRules = [];
		this.questionConditionMap = {};
		this.questionEffectsMap = {};

		if (this.logic === null) {
			return;
		}

		for (const logicRule of this.logic) {
			if (!isLogicRuleValid(logicRule)) {
				continue;
			}

			this.validRules.push(logicRule);

			for (const condition of logicRule.conditions) {
				if (condition.sourceType === 'question') {
					if (!(condition.sourceUuid in this.questionConditionMap)) {
						this.questionConditionMap[condition.sourceUuid] = [];
					}

					this.questionConditionMap[condition.sourceUuid].push(logicRule);
				}
			}
		}
	}

	evaluateAll(): void {
		if (this.logic === null) {
			return;
		}

		for (const rule of this.logic) {
			this.evaluateRule(rule);
		}
	}

	evaluateMetaVariableCondition(
		metaVariableUuid: string,
		comparator: ConditionComparatorsType,
		value: string | number | string[] | number[] | null
	): boolean {
		const responseStore = useResponseStore();
		const responseGroup = responseStore.responseGroup!;

		const responseValue = responseGroup.getMetaVariable(metaVariableUuid);

		return genericEvaluator(
			value,
			responseValue,
			comparator
		);
	}

	evaluateQuestionCondition(
		questionUuid: string,
		comparator: ConditionComparatorsType,
		value: string | number | string[] | number[] | null,
		sourceSubVariable?: string | string[]
	): boolean {
		const responseStore = useResponseStore();
		const questionResponse = responseStore.responseGroup!.getResponse(questionUuid);

		const question = responseStore.getSeenQuestion(questionUuid);

		if (question === null) {
			console.warn("Question in condition for rule is not seen yet, short circuiting");
			// Might need to do something else here to flag
			// that it's not false its unknown as the question hasnt
			// appeared to the user yet
			return false;
		}

		let responseValue: string | number | string[] | number[] | null = null;
		if (questionResponse === null) {
			responseValue = null;
		} else if ([
			QuestionType.NPS, QuestionType.RATING, QuestionType.STAR_RATING
		].includes(question.questionType)) {
			responseValue = questionResponse.value;
		} else if ([
			QuestionType.DROPDOWN, QuestionType.SINGLE_CHOICE, QuestionType.MULTIPLE_CHOICE
		].includes(question.questionType)) {
			if (questionResponse.choices === null) {
				responseValue = null;
			} else {
				responseValue = questionResponse.choices.map(
					(cr: ChoiceResponse) => cr.choiceUuid
				);
			}

			if (responseValue && responseValue.length > 0 && question.questionType === QuestionType.SINGLE_CHOICE) {
				responseValue = responseValue[0];
			}
		} else if ([
			QuestionType.SHORT_TEXT_RESPONSE, QuestionType.TEXT_RESPONSE
		].includes(question.questionType)) {
			responseValue = questionResponse.text;
		} else if ([
			QuestionType.MATRIX
		].includes(question.questionType) && sourceSubVariable && !Array.isArray(sourceSubVariable)) {
			if (questionResponse.subResponses === null) {
				responseValue = null;
			} else {
				const matchedResponses = questionResponse.subResponses.filter(
					(sr: SubResponseType) => sr.subEntryUuid === sourceSubVariable
				);

				if (matchedResponses && matchedResponses.length > 0) {
					responseValue = matchedResponses.map((sr: SubResponseType) => sr.subQuestionUuid);
				}
			}
		} else if ([
			QuestionType.RANKING
		].includes(question.questionType) && sourceSubVariable) {
			if (questionResponse.choices === null) {
				responseValue = null;
			} else {
				const subVariables = (Array.isArray(sourceSubVariable))? sourceSubVariable : [sourceSubVariable];
				const matchedChoices = questionResponse.choices.filter(
					(c: ChoiceResponse) => subVariables.includes(c.choiceUuid) && c.rank !== null
				);

				responseValue = matchedChoices.map((c: ChoiceResponse) => c.rank!);
			}
		}

		if (question.questionType === QuestionType.NPS) {
			if (!Array.isArray(value)) {
				if (value === 'detractors') {
					value = [0, 1, 2, 3, 4, 5, 6];
				} else if (value === 'passives') {
					value = [7, 8];
				} else if (value === 'promoters') {
					value = [9, 10];
				}
			} else {
				let npsValues: Set<number> = new Set([]);
				for (const val of value) {
					if (val === 'detractors') {
						npsValues = npsValues.union(new Set([0, 1, 2, 3, 4, 5, 6]));
					} else if (val === 'passives') {
						npsValues = npsValues.union(new Set([7, 8]));
					} else if (val === 'promoters') {
						npsValues = npsValues.union(new Set([9, 10]));
					} else {
						npsValues = npsValues.add(val as unknown as number);
					}
				}
				value = Array.from(npsValues);
			}

			if (['=', '!='].includes(comparator)) {
				comparator = (comparator === '=')? 'in' : 'not in';
			}
		} else if (question.questionType === QuestionType.RANKING) {
			if (Array.isArray(value)) {
				//@ts-ignore
				value = value.map((v: string) => parseInt(v));
			}
		}

		return genericEvaluator(
			value, responseValue, comparator
		);
	}

	evaluatePageCondition(
		questionUuid: string,
		comparator: ConditionComparatorsType,
		value: string | number | string[] | null
	): boolean {
		// Currently the only page condition is:
		//	"is page finished" which is only truthy
		//	server side on valid submit
		//	so always return false in js
		return false;
	}

	evaluateCondition(condition: SurveyLogicCondition): boolean {
		let result = false;

		switch(condition.sourceType) {
			case 'question':
				result = this.evaluateQuestionCondition(
					condition.sourceUuid, condition.comparator, condition.value, condition.sourceSubVariable
				);
			break;
			case 'page':
				result = this.evaluatePageCondition(
					condition.sourceUuid, condition.comparator, condition.value
				);
			break;
			case 'metavar':
				result = this.evaluateMetaVariableCondition(
					condition.sourceUuid, condition.comparator, condition.value
				);
			break;
			default:
				assertUnreachable(condition.sourceType);
		}

		return result;
	}

	evaluateRule(rule: SurveyLogicRule): void {
		let conditionsPassed = false;

		if (rule.conditionLogic === undefined || rule.conditionLogic === 'and') {
			conditionsPassed = rule.conditions.every(
				(rc: SurveyLogicCondition) => this.evaluateCondition(rc)
			);
		} else if (rule.conditionLogic === 'or') {
			conditionsPassed = rule.conditions.some(
				(rc: SurveyLogicCondition) => this.evaluateCondition(rc)
			);
		}

		for (const action of rule.actions) {
			if (action === null) {
				continue;
			}

			this.runAction(
				rule, action, conditionsPassed
			);
		}
	}

	_actionShow(
		rule: SurveyLogicRule,
		action: SurveyLogicAction,
		conditionsPassed: boolean
	): void {
		if (action.target === undefined || action.target === null) {
			return;
		}

		const targets = (Array.isArray(action.target))? action.target : [action.target];

		for (const target of targets) {
			if (target.type !== 'question') {
				continue;
			}

			this.addOrUpdateQuestionEffectsMap(
				target.questionUuid, {visible: (conditionsPassed)? true : false}
			)
			this.questionEffectsMap[target.questionUuid];
		}
	}

	_actionHide(
		rule: SurveyLogicRule,
		action: SurveyLogicAction,
		conditionsPassed: boolean
	): void {
		if (action.target === undefined || action.target === null) {
			return;
		}

		const targets = (Array.isArray(action.target))? action.target : [action.target];

		for (const target of targets) {
			if (target.type !== 'question') {
				continue;
			}

			this.addOrUpdateQuestionEffectsMap(
				target.questionUuid, {visible: (conditionsPassed)? false : true}
			)
			this.questionEffectsMap[target.questionUuid];
		}
	}

	_actionSkip(
		rule: SurveyLogicRule,
		action: SurveyLogicAction,
		conditionsPassed: boolean
	): void {
		// This is handled server side
		//  placeholder for completness and if we ever want to react
		//	locally
	}

	_actionMetavar(
		rule: SurveyLogicRule,
		action: SurveyLogicAction,
		conditionsPassed: boolean
	): void {
		const responseStore = useResponseStore();
		const responseGroup = responseStore.responseGroup!;

		if (action.config && conditionsPassed && action.target && !Array.isArray(action.target) && action.target.type === 'metavar') {
			const variableUuid = action.target.metaVariableUuid;
			let currentValue: number | string | null = null;
			switch (action.config.action) {
				case 'clear':
					responseGroup.setMetaVariable(variableUuid, null);
				break;
				case 'set':
					if (action.config.value !== undefined) {
						responseGroup.setMetaVariable(variableUuid, action.config.value);
					}
				break;
				case 'increment':
					currentValue = responseGroup.getMetaVariable(variableUuid);
					if (currentValue !== null && typeof currentValue === 'number') {
						responseGroup.setMetaVariable(variableUuid, currentValue+1);
					}
				break;
				case 'decrement':
					currentValue = responseGroup.getMetaVariable(variableUuid);
					if (currentValue !== null && typeof currentValue === 'number') {
						responseGroup.setMetaVariable(variableUuid, currentValue-1);
					}
				break;
			}
		}
	}

	addOrUpdateQuestionEffectsMap(
		questionUuid: string,
		updateKeys: Partial<QuestionEffect>
	): void {
		if (!(questionUuid in this.questionEffectsMap)) {
			this.questionEffectsMap[questionUuid] = {
				questionUuid,
				visible: true
			};
		}

		this.questionEffectsMap[questionUuid] =
			{ ...this.questionEffectsMap[questionUuid], ...updateKeys };

		this.events.emit('question:effect', {
			uuid: questionUuid,
			effect: this.questionEffectsMap[questionUuid]
		});
	}

	runAction(
		rule: SurveyLogicRule,
		action: SurveyLogicAction,
		conditionsPassed: boolean
	): void {
		switch(action.action) {
			case 'show':
				this._actionShow(rule, action, conditionsPassed);
			break;
			case 'hide':
				this._actionHide(rule, action, conditionsPassed);
			break;
			case 'skip':
				this._actionSkip(rule, action, conditionsPassed);
			break;
			case 'metavar':
				this._actionMetavar(rule, action, conditionsPassed);
			break;
			default:
				assertUnreachable(action.action);
		}
	}

	responseChanged(questionUuid: string): void {
		const affectedRules = this.questionConditionMap[questionUuid];

		if (affectedRules === undefined) {
			return;
		}

		for (const rule of affectedRules) {
			this.evaluateRule(rule);
		}
	}
}
